import { useZakeke, ZakekeViewer } from '@zakeke/zakeke-configurator-react';
import { Button } from 'components/Atomic';
import ArDeviceSelectionDialog from 'components/dialogs/ArDeviceSelectionDialog';
import RecapPanel from 'components/widgets/RecapPanel';
import {
	findAttribute,
	findGroup,
	findStep,
	launchFullscreen,
	quitFullscreen,
	useActualGroups,
	useUndoRedoActions
} from 'Helpers';
import { UndoRedoStep } from 'Interfaces';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useStore from 'Store';
import { ReactComponent as BarsSolid } from '../assets/icons/bars-solid.svg';
import { ReactComponent as CollapseSolid } from '../assets/icons/compress-arrows-alt-solid.svg';
import { ReactComponent as DesktopSolid } from '../assets/icons/desktop-solid.svg';
import { ReactComponent as ExplodeSolid } from '../assets/icons/expand-arrows-alt-solid.svg';
import { ReactComponent as ExpandSolid } from '../assets/icons/expand-solid.svg';

import AiDialog from 'components/dialogs/AIDialog';
import { AIProductStaging } from 'components/ai-studio';
import TryOnsButton from 'components/widgets/TryOnsButtons';
import { DesignWithAIButton } from 'components/widgets/UnifiedAIButton';
import { ViewerModeTabs } from 'components/widgets/ViewerModeTabs';
import { ReactComponent as RedoSolid } from '../assets/icons/redo-solid.svg';
import { ReactComponent as ResetSolid } from '../assets/icons/reset-alt-solid.svg';
import { ReactComponent as SearchMinusSolid } from '../assets/icons/search-minus-solid.svg';
import { ReactComponent as SearchPlusSolid } from '../assets/icons/search-plus-solid.svg';
import { ReactComponent as UndoSolid } from '../assets/icons/undo-solid.svg';
import { Dialog, useDialogManager } from './dialogs/Dialogs';
import {
	ArIcon,
	BottomRightIcons,
	CollapseIcon,
	DialogWindowDecorator,
	ExplodeIcon,
	FullscreenIcon,
	RecapPanelIcon,
	RedoIcon,
	ResetIcon,
	SecondScreenIcon,
	SpanAndButtonContainer,
	TopRightIcons,
	UndoIcon,
	ViewerContainer,
	ZoomInIcon,
	ZoomOutIcon
} from './layout/SharedComponents';
import Notifications from './widgets/Notifications';

// Styled component for the container of the 3D view.
const Viewer3D = () => {
	const ref = useRef<HTMLDivElement | null>(null);
	const {
		userSettings,
		isSceneLoading,
		IS_IOS,
		IS_ANDROID,
		getMobileArUrl,
		openArMobile,
		isSceneArEnabled,
		zoomIn,
		zoomOut,
		sellerSettings,
		reset,
		openSecondScreen,
		product,
		hasExplodedMode,
		setExplodedMode,
		hasVTryOnEnabled,
		getTryOnSettings,
		isInfoPointContentVisible,
		isAIStudioEnabled,
		isAiEditorEnabled,
		isAiProductStagingEnabled,
		isAiVtoEnabled,
	} = useZakeke();

	const [isRecapPanelOpened, setRecapPanelOpened] = useState(
		sellerSettings?.isCompositionRecapVisibleFromStart ?? false
	);

	const { showDialog, closeDialog } = useDialogManager();
	const { setIsLoading, notifications, removeNotification, isDraftEditor, isEditorMode, aiStudioViewerContainerId } =
		useStore();

	// State to track if the AI Studio container is available
	const [aiStudioContainer, setAIStudioContainer] = useState<HTMLElement | null>(null);

	// Check if the AI Studio container exists in the DOM
	useEffect(() => {
		if (aiStudioViewerContainerId) {
			console.log('[Viewer3D] Looking for container:', aiStudioViewerContainerId);

			let attempts = 0;
			const maxAttempts = 10;

			const checkContainer = () => {
				const container = document.getElementById(aiStudioViewerContainerId);
				console.log('[Viewer3D] Attempt', attempts + 1, 'container found:', !!container);

				if (container) {
					setAIStudioContainer(container);
					console.log('[Viewer3D] Container set successfully');
				} else if (attempts < maxAttempts) {
					attempts++;
					setTimeout(checkContainer, 100);
				} else {
					console.error('[Viewer3D] Failed to find container after', maxAttempts, 'attempts');
				}
			};

			// Check immediately
			checkContainer();

			return () => {
				console.log('[Viewer3D] Cleaning up container reference');
			};
		} else {
			console.log('[Viewer3D] No container ID, clearing container');
			setAIStudioContainer(null);
		}
	}, [aiStudioViewerContainerId]);

	// HOTFIX for Coutie while RapidCompact resolves the issue with iOS AR
	const isCoutie = userSettings?.userID === 271265;
	useEffect(() => {
		if (sellerSettings && sellerSettings?.isCompositionRecapVisibleFromStart)
			setRecapPanelOpened(sellerSettings.isCompositionRecapVisibleFromStart);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sellerSettings]);

	const switchFullscreen = () => {
		if (
			(document as any).fullscreenElement ||
			(document as any).webkitFullscreenElement ||
			(document as any).mozFullScreenElement ||
			(document as any).msFullscreenElement
		) {
			quitFullscreen(ref.current!);
		} else {
			launchFullscreen(ref.current!);
		}
	};

	const handleArClick = async () => {
		if (IS_ANDROID || IS_IOS) {
			setIsLoading(true);
			const url = await getMobileArUrl();
			setIsLoading(false);
			if (url)
				if (IS_IOS && !isCoutie) {
					openArMobile(url as string);
				} else if (IS_ANDROID || isCoutie) {
					showDialog(
						'open-ar',
						<Dialog
							windowDecorator={DialogWindowDecorator}
							onClose={() => closeDialog('open-ar')}
							lighterOverlay={true}
						>
							<SpanAndButtonContainer>
								<span>Bring it to life in your space!</span>
								<Button
									style={{ display: 'block', width: '80%' }}
									onClick={() => {
										closeDialog('open-ar');
										openArMobile(url as string);
									}}
								>
									View in AR
								</Button>
							</SpanAndButtonContainer>
						</Dialog>
					);
				}
		} else {
			showDialog('select-ar', <ArDeviceSelectionDialog />);
		}
	};

	const { setIsUndo, undoStack, setIsRedo, redoStack } = useStore();
	const undoRedoActions = useUndoRedoActions();

	const handleUndoClick = () => {
		setIsUndo(true);

		let actualUndoStep = undoStack.length > 0 ? undoStack.pop() : null;
		if (actualUndoStep && actualUndoStep.length > 0) {
			undoRedoActions.fillRedoStack(actualUndoStep);
			actualUndoStep
				.filter((x: UndoRedoStep) => x.direction === 'undo')
				.forEach((singleStep: UndoRedoStep) => handleUndoSingleStep(singleStep));
		}

		setIsUndo(false);
	};

	const { undo, redo } = useZakeke();
	const { setSelectedGroupId, setSelectedStepId, setSelectedAttributeId, isMobile } = useStore();

	const actualGroups = useActualGroups() ?? [];

	const handleUndoSingleStep = (actualUndoStep: UndoRedoStep) => {
		if (actualUndoStep.id === null && !isMobile) return;
		if (actualUndoStep.type === 'group')
			return setSelectedGroupId(findGroup(actualGroups, actualUndoStep.id)?.id ?? null);
		if (actualUndoStep.type === 'step')
			return setSelectedStepId(findStep(actualGroups, actualUndoStep.id)?.id ?? null);
		if (actualUndoStep.type === 'attribute')
			return setSelectedAttributeId(findAttribute(actualGroups, actualUndoStep.id)?.id ?? null);
		if (actualUndoStep.type === 'option') {
			return undo();
		}
	};

	const handleRedoClick = () => {
		setIsRedo(true);

		let actualRedoStep = redoStack.length > 0 ? redoStack.pop() : null;
		if (actualRedoStep != null) {
			undoRedoActions.fillUndoStack(actualRedoStep);
			actualRedoStep
				.filter((x: UndoRedoStep) => x.direction === 'redo')
				.forEach(async (singleStep: UndoRedoStep) => handleRedoSingleStep(singleStep));
		}

		setIsRedo(false);
	};

	const handleRedoSingleStep = (actualRedoStep: { type: string; id: number | null; direction: string }) => {
		if (actualRedoStep.id === null && !isMobile) return;
		if (actualRedoStep.type === 'group')
			return setSelectedGroupId(findGroup(actualGroups, actualRedoStep.id)?.id ?? null);
		if (actualRedoStep.type === 'step')
			return setSelectedStepId(findStep(actualGroups, actualRedoStep.id)?.id ?? null);
		else if (actualRedoStep.type === 'attribute')
			return setSelectedAttributeId(findAttribute(actualGroups, actualRedoStep.id)?.id ?? null);
		else if (actualRedoStep.type === 'option') return redo();
	};

	// Render the ZakekeViewer in the AI Studio container if it's open, otherwise render it here
	// Always create the viewer element - ZakekeViewer will handle its own loading state
	const viewerElement = <ZakekeViewer bgColor='#f2f2f2' />;

	// Debug: log portal state
	useEffect(() => {
		console.log(
			'[Viewer3D] Portal state - aiStudioContainer:',
			!!aiStudioContainer,
			'isSceneLoading:',
			isSceneLoading
		);
		if (aiStudioContainer) {
			console.log('[Viewer3D] Creating portal to container:', aiStudioContainer);
			console.log('[Viewer3D] Container dimensions:', {
				width: aiStudioContainer.offsetWidth,
				height: aiStudioContainer.offsetHeight,
				clientWidth: aiStudioContainer.clientWidth,
				clientHeight: aiStudioContainer.clientHeight
			});
		}
	}, [aiStudioContainer, isSceneLoading]);

	return (
		<ViewerContainer ref={ref}>
			{aiStudioContainer ? createPortal(viewerElement, aiStudioContainer) : viewerElement}

			{!isInfoPointContentVisible && (
				<>
					<ZoomInIcon id='btn_zoom_in' $isMobile={isMobile} key={'zoomin'} hoverable onClick={zoomIn}>
						<SearchPlusSolid />
					</ZoomInIcon>
					<ZoomOutIcon id='btn_zoom_out' $isMobile={isMobile} key={'zoomout'} hoverable onClick={zoomOut}>
						<SearchMinusSolid />
					</ZoomOutIcon>
					{sellerSettings?.canUndoRedo && (
						<ResetIcon id='btn_reset' $isMobile={isMobile} key={'reset'} hoverable onClick={reset}>
							<ResetSolid />
						</ResetIcon>
					)}
					{sellerSettings?.canUndoRedo && (
						<UndoIcon id='btn_undo' $isMobile={isMobile} key={'undo'} hoverable onClick={handleUndoClick}>
							<UndoSolid />
						</UndoIcon>
					)}
					{sellerSettings?.canUndoRedo && (
						<RedoIcon id='btn_redo' $isMobile={isMobile} key={'redo'} hoverable onClick={handleRedoClick}>
							<RedoSolid />
						</RedoIcon>
					)}
					{!isSceneLoading && hasVTryOnEnabled && !isDraftEditor && !isEditorMode && !isAiEditorEnabled && (
						<TryOnsButton settings={getTryOnSettings()} />
					)}
					<BottomRightIcons>
						{hasExplodedMode() && product && !isSceneLoading && (
							<>
								<CollapseIcon id='btn_collapse' hoverable onClick={() => setExplodedMode(false)}>
									<CollapseSolid />
								</CollapseIcon>
								<ExplodeIcon id='btn_explode' hoverable onClick={() => setExplodedMode(true)}>
									<ExplodeSolid />
								</ExplodeIcon>
							</>
						)}

						{product && product.isShowSecondScreenEnabled && !isDraftEditor && !isEditorMode && (
							<SecondScreenIcon id='btn_second_screen' key={'secondScreen'} hoverable onClick={openSecondScreen}>
								<DesktopSolid />
							</SecondScreenIcon>
						)}

						{!IS_IOS && (
							<FullscreenIcon id='btn_fullscreen'
								className='fullscreen-icon'
								key={'fullscreen'}
								hoverable
								onClick={switchFullscreen}
							>
								<ExpandSolid />
							</FullscreenIcon>
						)}
					</BottomRightIcons>
					<TopRightIcons>
						{/* Design with AI button */}
						{!isDraftEditor && !isEditorMode && (
							<DesignWithAIButton id='btn_design_with_ai'
								$isArIconVisible={isSceneArEnabled()}
								onClick={() => showDialog('ai', <AiDialog />)}
							/>
						)}
						{isSceneArEnabled() && !isDraftEditor && !isEditorMode && !isAiEditorEnabled && (
							<ArIcon id='btn_ar' onClick={() => handleArClick()} />
						)}
					</TopRightIcons>
					{isAIStudioEnabled && (isAiProductStagingEnabled || isAiVtoEnabled) && !isAiEditorEnabled && !isDraftEditor && !isEditorMode && (
						<ViewerModeTabs
							onOpenAIStudio={(mode) =>
								showDialog(
									'ai-studio',
									<AIProductStaging
										onClose={() => closeDialog('ai-studio')}
										initialMode={mode === 'staging' ? 'product-staging' : 'virtual-tryon'}
									/>
								)
							}
							isProductStagingEnabled={isAiProductStagingEnabled}
							isVtoEnabled={isAiVtoEnabled}
						/>
					)}
					{sellerSettings?.isCompositionRecapEnabled && (
						<RecapPanelIcon id='btn_recap' key={'recap'} onClick={() => setRecapPanelOpened(!isRecapPanelOpened)}>
							<BarsSolid />
						</RecapPanelIcon>
					)}
					{sellerSettings?.isCompositionRecapEnabled && isRecapPanelOpened && (
						<RecapPanel key={'recapPanel'} onClose={() => setRecapPanelOpened(false)} />
					)}{' '}
				</>
			)}

			{/* Notifications */}
			<Notifications
				notifications={notifications}
				onRemoveNotificationClick={(notification) => removeNotification(notification.id)}
			/>
		</ViewerContainer>
	);
};

export default Viewer3D;
